/**
 * @file PrivacyPolicyPage.jsx
 * @description Official privacy policy for SimpleCode. Explains data collection, 
 *              usage, and user rights in a clean, Bento-inspired light theme.
 * @module components/PrivacyPolicyPage
 */
import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import Navbar from './layout/Navbar';
import Footer from './layout/Footer';
import {
    FiShield,
    FiChevronDown,
    FiChevronRight,
    FiArrowRight
} from 'react-icons/fi';

/* ══════════════════════════════════════════════════════════════
 * MAIN COMPONENT
 * ══════════════════════════════════════════════════════════════ */

/**
 * @component PrivacyPolicyPage
 * @description Renders the platform's privacy policy with an interactive, categorized layout.
 *
 * @param {Object} props
 * @param {Function} props.navigate - Router navigation function.
 * @returns {JSX.Element} The rendered Privacy Policy page.
 */
const PrivacyPolicyPage = ({ navigate }) => {
    const { isAuthenticated } = useAuth();
    // ── Component State ──
    const [expandedPolicy, setExpandedPolicy] = useState(null);
    const [loading, setLoading] = useState(true);

    /* ══════════════════════════════════════════════════════════════
     * DATA & CONFIG
     * ══════════════════════════════════════════════════════════════ */

    // ── Navigation Links (Synchronized across the platform) ──
    const navLinks = [
        { id: "home", label: "Home", action: () => navigate("/") },
        { id: "learning", label: "Learning", action: () => navigate("/learning") },
        { id: "dashboard", label: "Dashboard", action: () => navigate("/dashboard"), show: isAuthenticated },
        { id: "dsa", label: "DSA Problems", action: () => navigate("/dsa-problems") },
        { id: "system-design", label: "System Design", action: () => navigate("/system-design") }
    ].filter(link => link.show !== false);

    const footerLinks = [
        { label: "Home", action: () => navigate("/") },
        { label: "DSA Problems", action: () => navigate("/dsa-problems") },
        { label: "System Design", action: () => navigate("/system-design") }
    ];

    useEffect(() => {
        window.scrollTo(0, 0);
        document.title = "SimpleCode | Privacy Policy";
        // Emulate a light loading state for smooth entry
        const timer = setTimeout(() => setLoading(false), 400);
        return () => {
            clearTimeout(timer);
            document.title = "SimpleCode";
        };
    }, []);

    /**
     * @constant policies
     * @description Complete list of privacy policy clauses displayed in the accordion.
     * @type {Array<{title: string, content: string}>}
     */
    const policies = [
        {
            title: 'Information We Collect',
            content: 'We collect information you provide directly, including your name, email address, and profile details when you sign in using Google OAuth.'
        },
        {
            title: 'Google OAuth Data',
            content: 'When you sign in with Google, we receive your name, email address, and profile picture. We do not access your Google password or any other Google account data.'
        },
        {
            title: 'Code Submissions',
            content: 'We store your code submissions, including source code, language preference, and submission results, to provide personalized learning analytics and track your progress.'
        },
        {
            title: 'Usage Analytics',
            content: 'We collect anonymous usage data such as pages visited, features used, and time spent on the platform to improve our services and user experience.'
        },
        {
            title: 'Cookies and Local Storage',
            content: 'We use cookies and browser local storage to maintain your session, remember your preferences (such as editor theme and font size), and provide a seamless experience.'
        },
        {
            title: 'Third-Party Services',
            content: 'We use third-party services including Google OAuth for authentication and database management. These services have their own privacy policies that govern their use of your data.'
        },
        {
            title: 'Data Storage and Security',
            content: 'Your data is stored securely using industry-standard encryption and security practices. We use secure HTTPS connections for all data transfers.'
        },
        {
            title: 'How We Use Your Information',
            content: 'We use your information to provide and improve our services, personalize your learning experience, track your progress, and communicate important updates.'
        },
        {
            title: 'Progress Tracking',
            content: 'We track your learning progress, completed topics, and submission history to provide personalized recommendations and display your achievements on your dashboard.'
        },
        {
            title: 'Communication',
            content: 'We may use your email address to send important service updates, security alerts, and platform announcements. We do not send marketing emails without your consent.'
        },
        {
            title: 'Data Sharing',
            content: 'We do not sell, trade, or rent your personal information to third parties. We may share anonymized, aggregated data for analytical purposes.'
        },
        {
            title: 'Batch and Group Features',
            content: 'If you participate in batch learning, your progress and submission data may be visible to batch administrators for mentoring and tracking purposes.'
        },
        {
            title: 'Data Retention',
            content: 'We retain your account data for as long as your account is active. You may request deletion of your account and associated data at any time.'
        },
        {
            title: 'User Rights',
            content: 'You have the right to access, correct, update, or request deletion of your personal information. Contact us to exercise any of these rights.'
        },
        {
            title: 'Account Deletion',
            content: 'You may request complete deletion of your account and all associated data by contacting us through our contact page. Deletion requests are processed within 30 days.'
        },
        {
            title: 'Children\'s Privacy',
            content: 'Our platform is not intended for children under 13 years of age. We do not knowingly collect personal information from children under 13.'
        },
        {
            title: 'Code Execution',
            content: 'Code you submit for execution is processed in isolated, sandboxed environments. We do not store execution logs beyond what is necessary for displaying results.'
        },
        {
            title: 'System Design Content',
            content: 'Your interaction with system design content, including reading progress and completion status, is tracked to provide personalized learning paths.'
        },
        {
            title: 'Browser Information',
            content: 'We may collect browser type, version, and device information to optimize the platform experience and troubleshoot technical issues.'
        },
        {
            title: 'IP Address',
            content: 'We may log IP addresses for security purposes, including preventing abuse, detecting unauthorized access, and ensuring platform integrity.'
        },
        {
            title: 'Data Portability',
            content: 'You have the right to request a copy of your personal data in a structured, machine-readable format. Contact us to request your data export.'
        },
        {
            title: 'Security Measures',
            content: 'We implement appropriate technical and organizational security measures including encryption, access controls, and regular security audits to protect your data.'
        },
        {
            title: 'Breach Notification',
            content: 'In the event of a data breach that affects your personal information, we will notify you within 72 hours through email and platform notifications.'
        },
        {
            title: 'Changes to This Policy',
            content: 'We may update this privacy policy from time to time. We will notify you of any significant changes by posting a notice on our platform or sending you an email.'
        },
        {
            title: 'Contact Us',
            content: 'If you have any questions about this privacy policy or our data practices, please contact us through our contact page or email us at support@simplecode.co.in.'
        }
    ];

    if (loading) {
        return (
            <div className="min-h-screen bg-accent flex flex-col font-sans">
                <Navbar navigate={navigate} navLinks={navLinks} />
                <main className="flex-1 pt-16">
                    <div className="max-w-8xl mx-auto px-4 sm:px-12 lg:px-16 animate-pulse mt-8">
                        <div className="h-10 w-64 bg-emerald-100 rounded mb-8" />
                        <div className="bg-white rounded-lg p-8 shadow-sm space-y-6">
                            {[...Array(6)].map((_, i) => (
                                <div key={i} className="border-b border-gray-100 pb-4">
                                    <div className="h-6 w-1/3 bg-gray-100 rounded mb-2" />
                                    <div className="h-4 w-full bg-gray-50 rounded" />
                                </div>
                            ))}
                        </div>
                    </div>
                </main>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-accent text-foreground flex flex-col font-sans">
            <Navbar navigate={navigate} navLinks={navLinks} />

            <main className="flex-1 pt-16">

                {/* ── Privacy Header ── */}
                <section className="bg-gradient-to-r from-emerald-50 via-teal-50 to-emerald-50 mb-8 mt-4 border-b border-emerald-100/30">
                    <div className="max-w-8xl mx-auto px-4 sm:px-12 lg:px-16 py-8">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                            <h1 className="text-left text-3xl font-bold text-foreground underline decoration-dotted decoration-foreground underline-offset-8">
                                Privacy <span className="bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent underline decoration-dotted decoration-emerald-600 ml-1">Policy</span>
                            </h1>
                            <p className="text-muted-foreground text-sm font-medium text-left">Last updated: March 2026</p>
                        </div>
                    </div>
                </section>

                <div className="max-w-8xl mx-auto px-4 sm:px-12 lg:px-16 pb-20">
                    <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">

                        {/* ── Left Side: Sidebar Cards (Sticky on Desktop) ── */}
                        <div className="lg:col-span-1 lg:sticky lg:top-24 lg:self-start space-y-6 pb-12">
                            <div className="bg-white border-l-4 border-emerald-600 rounded p-6 shadow-sm relative overflow-hidden">
                                <div className="relative z-10">
                                    <h2 className="text-lg font-bold text-emerald-600 mb-3">Our Commitment</h2>
                                    <p className="text-muted-foreground text-xs leading-relaxed text-left text-justify">
                                        At SimpleCode, we are committed to protecting your privacy and ensuring the security of your personal information.
                                        This policy explains how we collect and use your data when you use our platform.
                                    </p>
                                </div>
                            </div>

                            <div className="flex-col items-center justify-between p-6 bg-emerald-600 rounded text-white shadow-md space-y-2">
                                <div>
                                    <h4 className="text-lg font-bold">Have Questions?</h4>
                                    <p className="text-emerald-50 text-xs text-justify my-2">Reach out to our support team for any privacy concerns.</p>
                                </div>
                                <button
                                    onClick={() => navigate('/support')}
                                    className="w-full justify-center bg-white text-emerald-600 px-5 py-2 rounded font-bold text-xs hover:bg-emerald-50 transition-all flex items-center gap-2"
                                >
                                    CONTACT US <FiArrowRight className="w-4 h-4" />
                                </button>
                            </div>
                        </div>

                        {/* ── Right Side: Substantive Content (Main Scroll) ── */}
                        <div className="lg:col-span-3">
                            <div className="bg-white border-l-4 border-emerald-600 rounded overflow-hidden relative">

                                {/* Background Watermark */}
                                <div className="hidden md:block absolute right-[-20px] top-[-20px] opacity-[0.15] transform rotate-12 transition-opacity duration-300 pointer-events-none z-20">
                                    <FiShield className="w-96 h-96" />
                                </div>

                                <div className="relative z-10 divide-y divide-border-border">
                                    {policies.map((policy, index) => {
                                        const isExpanded = expandedPolicy === index;
                                        return (
                                            <div key={index} className="group transition-colors duration-200 hover:bg-emerald-50/60">
                                                <button
                                                    onClick={() => setExpandedPolicy(isExpanded ? null : index)}
                                                    className="w-full flex items-center justify-between p-6 text-left focus:outline-none"
                                                >
                                                    <div className="flex items-center gap-4 hover:text-emerald-600">
                                                        <span className="text-sm font-bold text-emerald-600 group-hover:text-emerald-600 transition-colors w-6">
                                                            {String(index + 1).padStart(2, '0')}
                                                        </span>
                                                        <h3 className={`text-base font-bold transition-colors group-hover:text-emerald-600 ${isExpanded ? 'text-emerald-700' : 'text-foreground'}`}>
                                                            {policy.title}
                                                        </h3>
                                                    </div>
                                                    {isExpanded ? (
                                                        <FiChevronDown className="w-5 h-5 text-emerald-600" />
                                                    ) : (
                                                        <FiChevronRight className="w-5 h-5 text-gray-400 group-hover:text-emerald-600" />
                                                    )}
                                                </button>

                                                <div
                                                    className={`grid transition-[grid-template-rows] duration-300 ease-in-out ${isExpanded ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'}`}
                                                >
                                                    <div className="overflow-hidden">
                                                        <div className="px-16 pb-6 text-sm text-muted-foreground leading-relaxed text-justify">
                                                            {policy.content}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </main>

            <Footer navigate={navigate} navLinks={footerLinks} />
        </div>
    );
};

export default PrivacyPolicyPage;

